/**
 * @file       Deliberations vuex store, used by the whole deliberations module.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import axios from "@/plugins/axios.js";

/**
 * Gets the default state.
 *
 * @return  {Object}  The default state.
 */
function getDefaultState() {
  return {
    register: {
      status: null,
      data: null,
      error: null
    },
    config: {
      status: null,
      data: null,
      error: null
    },
    teacherDlibr: {
      status: null,
      data: { data: [] },
      error: null,
      filters: {
        resourcesPerPage: 10,
        status: null,
        id: null,
        teacher: null,
        name: null,
        group: null,
        page: 1
      }
    },
    participantDlibr: {
      status: null,
      data: null,
      error: null,
      filters: {
        status: null,
        id: null,
        user: null,
        group: null,
        resourcesPerPage: 20,
        page: 1
      }
    },
    dlibr: {
      status: null,
      data: null,
      error: null
    },
    keywordDlibr: {
      status: null,
      data: null,
      error: null
    },
    statsDlibr: {
      status: null,
      data: null,
      error: null
    },
    arborescenceDlibr: {
      status: null,
      data: null,
      error: null
    },
    pubToEdit: {
      mode: "",
      pub: {}
    }
  };
}

/**
 * Deliberations vuex actions.
 *
 * @type  {Object}
 */
const actions = {
  /**
   * Get participant deliberation
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getParticipantDlibr: ({ commit, state }) => {
    const payload = Object.entries(state.participantDlibr.filters).reduce(
      (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
      {}
    );
    return new Promise((resolve, reject) => {
      commit("onGetParticipantDlibr");

      axios
        .get(`teacher/deliberation`, { params: payload })
        .then(response => {
          commit("onGetParticipantDlibrSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetParticipantDlibrError", error);
          reject(error);
        });
    });
  },

  /**
   * Get teacher deliberation
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getTeacherDlibr: ({ commit, state }) => {
    return new Promise((resolve, reject) => {
      const payload = Object.entries(state.teacherDlibr.filters).reduce(
        (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
        {}
      );
      commit("onGetTeacherDlibr");
      axios
        .get(`teacher/deliberation`, { params: payload })
        .then(response => {
          commit("onGetTeacherDlibrSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetTeacherDlibrError", error);
          reject(error);
        });
    });
  },

  /**
   * Get the deliberation.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  getDlibr: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("getDlibr");

      axios
        .get(`teacher/deliberation`, params)
        .then(response => {
          commit("getDlibrSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("getDlibrError", error);
          reject(error);
        });
    });
  },

  /**
   * Put the deliberation.
   *
   * @param   {Function}  arg1.commit     Commit the action
   * @param   {Function}  arg1.dispatch   Dispatchthe action
   * @param   {Object}    payload         The payload
   *
   * @return  {Promise}
   */
  putDlibr: ({ commit, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPutDlibr");

      axios
        .put(`teacher/deliberation`, payload)
        .then(response => {
          commit("onPutDlibrSuccess", response);
          dispatch("getTeacherDlibr");
          resolve(response);
        })
        .catch(error => {
          commit("onPutDlibrError", error);
          reject(error);
        });
    });
  },

  /**
   * Post the deliberation.
   *
   * @param   {Function}  arg1.commit     Commit the action
   * @param   {Object}    payload         The payload
   *
   * @return  {Promise}
   */
  postDlibr: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPostDlibr");

      axios
        .post(`teacher/deliberation`, payload)
        .then(response => {
          commit("onPostDlibrSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPostDlibrError", error);
          reject(error);
        });
    });
  },

  /**
   * Delete the deliberation.
   *
   * @param   {Function}  arg1.commit     Commit the action
   * @param   {Object}    params          The parameters
   *
   * @return  {Promise}
   */
  deleteDlibr: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onDeleteDlibr");

      axios
        .patch(`teacher/deliberation/${params.id}`)
        .then(response => {
          commit("onDeleteDlibrSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onDeleteDlibrError", error);
          reject(error);
        });
    });
  },

  /**
   * Get deliberation keyword.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  getKeywordDlibr: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onGetKeywordDlibr");
      axios
        .get(`teacher/deliberation/keyword`, params)
        .then(response => {
          commit("onGetKeywordDlibrSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetKeywordDlibrError", error);
          reject(error);
        });
    });
  },

  /**
   * Search the deliberation keyword.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  searchKeywordDlibr: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onGetKeywordDlibr");
      axios
        .get(`teacher/deliberation/keyword`, params)
        .then(response => {
          commit("addGetKeywordDlibrSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetKeywordDlibrError", error);
          reject(error);
        });
    });
  },

  /**
   * Get deliberation arborescence.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  getArborescenceDlibr: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onGetArborescenceDlibr");
      axios
        .get(`teacher/deliberation/arborescence`, params)
        .then(response => {
          commit("onGetArborescenceDlibrSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetArborescenceDlibrError", error);
          reject(error);
        });
    });
  },

  /**
   * Get deliberation statistics.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  getStatsDlibr: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onGetStatsDlibr");
      axios
        .get(`teacher/deliberation/stats`, params)
        .then(response => {
          commit("onGetStatsDlibrSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetStatsDlibrError", error);
          reject(error);
        });
    });
  }
};

/**
 * Deliberations vuex mutations.
 *
 * @type  {Object}
 */
const mutations = {
  /**
   * Get participant deliberation.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetParticipantDlibr: state => {
    state.participantDlibr.status = "fetching";
    state.participantDlibr.error = {};
  },

  /**
   * Get participant deliberation success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetParticipantDlibrSuccess: (state, { data }) => {
    state.participantDlibr.status = "success";
    state.participantDlibr.error = null;
    state.participantDlibr.data = data;
  },

  /**
   * Get participant deliberation error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetParticipantDlibrError: (state, err) => {
    state.participantDlibr.status = "error";
    state.participantDlibr.error = err;
  },

  /**
   * Set participant deliberation filters.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  filters     The filters
   */
  setParticipantDlibrFilters: (state, filters) => {
    if (filters.reset) {
      state.participantDlibr.filters = {
        status: null,
        id: null,
        user: null,
        group: null,
        resourcesPerPage: 20,
        page: 1
      };
    }
    if (filters.fields) {
      state.participantDlibr.filters = {
        ...state.participantDlibr.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Get teacher deliberation.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetTeacherDlibr: state => {
    state.teacherDlibr.status = "fetching";
    state.teacherDlibr.error = {};
  },

  /**
   * Get teacher deliberation success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetTeacherDlibrSuccess: (state, { data }) => {
    state.teacherDlibr.status = "success";
    state.teacherDlibr.error = null;
    state.teacherDlibr.data = data;
  },

  /**
   * Get teacher deliberation error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetTeacherDlibrError: (state, err) => {
    state.teacherDlibr.status = "error";
    state.teacherDlibr.error = err;
  },

  /**
   * Get keyword deliberation.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetKeywordDlibr: state => {
    state.keywordDlibr.status = "fetching";
    state.keywordDlibr.error = {};
  },

  /**
   * Get keyword deliberation success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetKeywordDlibrSuccess: (state, { data }) => {
    state.keywordDlibr.status = "success";
    state.keywordDlibr.error = null;
    state.keywordDlibr.data = data;
  },

  /**
   * Add get keyword deliberation success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  addGetKeywordDlibrSuccess: (state, { data }) => {
    state.keywordDlibr.status = "success";
    state.keywordDlibr.error = null;
    state.keywordDlibr.data.data.keywords.push(data.data.find(d => d));
  },

  /**
   * Delete keyword deliberation.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  keyword     The keyword
   */
  deleteKeywordDlibr: (state, keyword) => {
    state.keywordDlibr.data.data.keywords = state.keywordDlibr.data.data.keywords.filter(
      item => {
        return item.keyword.keyword !== keyword;
      }
    );
  },

  /**
   * Get keyword deliberation error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetKeywordDlibrError: (state, err) => {
    state.keywordDlibr.status = "error";
    state.keywordDlibr.error = err;
  },

  /**
   * Get deliberation arboresence.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetArborescenceDlibr: state => {
    state.arborescenceDlibr.status = "fetching";
    state.arborescenceDlibr.error = {};
  },

  /**
   * Get deliberation arboresence success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetArborescenceDlibrSuccess: (state, { data }) => {
    state.arborescenceDlibr.status = "success";
    state.arborescenceDlibr.error = null;
    state.arborescenceDlibr.data = data;
  },

  /**
   * Get deliberation arboresence error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetArborescenceDlibrError: (state, err) => {
    state.arborescenceDlibr.status = "error";
    state.arborescenceDlibr.error = err;
  },

  /**
   * Get deliberation statistics
   *
   * @param  {Object}  state   The vuex state
   */
  onGetStatsDlibr: state => {
    state.statsDlibr.status = "fetching";
    state.statsDlibr.error = {};
  },

  /**
   * Get deliberation statistics success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetStatsDlibrSuccess: (state, { data }) => {
    state.statsDlibr.status = "success";
    state.statsDlibr.error = null;
    state.statsDlibr.data = data;
  },

  /**
   * Get deliberation statistics error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetStatsDlibrError: (state, err) => {
    state.statsDlibr.status = "error";
    state.statsDlibr.error = err;
  },

  /**
   * Get deliberation.
   *
   * @param  {Object}  state   The vuex state
   */
  getDlibr: state => {
    state.dlibr.status = "fetching";
    state.dlibr.error = {};
  },

  /**
   * Get deliberation success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  getDlibrSuccess: (state, { data }) => {
    state.dlibr.status = "success";
    state.dlibr.error = null;
    state.dlibr.data = data;
  },

  /**
   * Get deliberation error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  getDlibrError: (state, err) => {
    state.dlibr.status = "error";
    state.dlibr.error = err;
  },

  /**
   * Put deliberation.
   *
   * @param  {Object}  state   The vuex state
   */
  onPutDlibr: state => {
    state.dlibr.status = "fetching";
    state.dlibr.error = {};
  },

  /**
   * Put deliberation success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPutDlibrSuccess: (state, { data }) => {
    state.dlibr.status = "success";
    state.dlibr.error = null;
    state.dlibr.data = data;
  },

  /**
   * Put deliberation error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPutDlibrError: (state, err) => {
    state.dlibr.status = "error";
    state.dlibr.error = err;
  },

  /**
   * Post deliberation.
   *
   * @param  {Object}  state   The vuex state
   */
  onPostDlibr: state => {
    state.dlibr.status = "fetching";
    state.dlibr.error = {};
  },

  /**
   * Post deliberation success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPostDlibrSuccess: (state, { data }) => {
    state.dlibr.status = "success";
    state.dlibr.error = null;
    state.dlibr.data = data;
  },

  /**
   * Post deliberation error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPostDlibrError: (state, err) => {
    state.dlibr.status = "error";
    state.dlibr.error = err;
  },

  /**
   * Delete deliberation.
   *
   * @param  {Object}  state   The vuex state
   */
  onDeleteDlibr: state => {
    state.dlibr.status = "fetching";
    state.dlibr.error = {};
  },

  /**
   * Delete deliberation success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onDeleteDlibrSuccess: (state, { data }) => {
    state.dlibr.status = "success";
    state.dlibr.error = null;
    state.dlibr.data = data;
  },

  /**
   * Delete deliberation error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onDeleteDlibrError: (state, err) => {
    state.dlibr.status = "error";
    state.dlibr.error = err;
  },

  /**
   * Set teacher deliberation filters.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  filters     The filters
   */
  setTeacherDlibrFilters: (state, filters) => {
    if (filters.reset) {
      state.teacherDlibr.filters = {
        resourcesPerPage: 10,
        status: null,
        id: null,
        group: null,
        teacher: null,
        name: null,
        page: 1
      };
    }
    if (filters.fields) {
      state.teacherDlibr.filters = {
        ...state.teacherDlibr.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Set pub to edit.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  payload     The payload
   */
  setPubToEdit: (state, payload) => {
    state.pubToEdit.mode = payload.mode;
    state.pubToEdit.pub = payload.pub;
  },

  /**
   * Set pub mode.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  mode        The mode
   */
  setPubMode: (state, mode) => {
    state.pubToEdit.mode = mode;
  },

  /**
   * Set teacher deliberation.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  payload     The payload
   */
  setTeacherDlibr: (state, payload) => {
    state.teacherDlibr.data = payload;
  }
};

/**
 * Export the store.
 *
 * @type  {Object}
 */
export default {
  namespaced: true,
  state: getDefaultState(),
  actions,
  mutations
};
